import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import {Card} from 'react-bootstrap';
import {ChangeEvent, FormEvent, useEffect, useState} from 'react';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import {boolean, object, string} from 'yup';
import {useFormik, getIn} from 'formik';
import {Link, useNavigate} from 'react-router'
import Logo from '../../ui/logos/Logo';
import {CheckList} from '../../ui/ListGroup/CheckList.tsx';
import Checkbox from '../../ui/form/Checkbox';
import {PasswordField} from '../../ui/form/login/PasswordField.tsx';
import AuthAPI from '../../../services/AuthAPI';
import AlertCustom from '../../ui/alert/AlertCustom';

export const UpdatePassword = () => {
    const [confirmAdminPasswordValid, setConfirmAdminPasswordValid] = useState<boolean>(false);
    const [confirmUserPasswordValid, setConfirmUserPasswordValid] = useState<boolean>(false);
    const [flashbag, setFlashbag] = useState<boolean>(false);
    const [flashbagMessage, setFlashbagMessage] = useState<string|Element>('');
    const [flashbagVariant, setFlashbagVariant] = useState<string>('text-danger');
    const [updatePasswordSuccess, setUpdatePasswordSuccess] = useState<boolean>(false);
    const globalpasswordContraint = /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[^A-Za-z0-9]).{12,}$/;
    const navigate = useNavigate();
    const passwordConstraints = [
        {
            label: '12 caractères minimum',
            regex: /.{12,}/,
        },
        {
            label: '1 majuscule minimum',
            regex: /[A-Z]/,
        },
        {
            label: '1 minuscule minimum',
            regex: /[a-z]/,
        },
        {
            label: '1 chiffre minimum',
            regex: /\d/,
        },
        {
            label: '1 caractère spécial minimum',
            regex: /[^A-Za-z0-9]/,
        },
    ];
    const validationSchema = object().shape({
        newAdminPassword: string()
            .min(12, 'Ce champ doit contenir au moins 12 caractères')
            .matches(globalpasswordContraint, 'Ce champ ne respecte pas les prérequis')
            .required('Champ requis'),
        confirmAdminPassword: string().required('Champ requis'),
        addAnotherPasswordForUser: boolean(),
        newUserPassword: string()
            .when('addAnotherPasswordForUser', {
                is: (value: boolean) => value === true,
                then: (schema) => schema
                    .min(12, 'Ce champ doit contenir au moins 12 caractères')
                    .matches(globalpasswordContraint, 'Ce champ ne respecte pas les prérequis')
                    .required('Champ requis'),
            }),
        confirmUserPassword: string()
            .when('addAnotherPasswordForUser', {
                is: (value: boolean) => value === true,
                then: (schema) => schema.required('Champ requis')
            }),
    });

    const formik = useFormik({
        initialValues: {
            newAdminPassword: '',
            confirmAdminPassword: '',
            addAnotherPasswordForUser: false,
            newUserPassword: '',
            confirmUserPassword: '',
        },
        validationSchema,
        validateOnChange: true,
        onSubmit: (values) => {
            const searchParams = new URLSearchParams(document.location.search);
            AuthAPI.updatePassword(values, searchParams.get('hash'))
                .then(() => {
                    setFlashbag(true);
                    setFlashbagVariant('success');
                    setFlashbagMessage('Mot(s) de passe modifié(s) avec succès.');
                    setUpdatePasswordSuccess(true);

                    setTimeout(() => {
                        navigate('/');
                    }, 3000);

                })
                .catch((error) => {
                    setFlashbag(true);
                    setFlashbagVariant('danger');

                    switch (error.response.status) {
                        case 401:
                            setFlashbagMessage('Vous n\'êtes pas autorisés à effectuer cette action');
                            break;
						case 403:
							setFlashbagMessage(
								<span>
									Votre demande de changement de mot de passe a expiré, merci de réessayer
									<Link
										to={{pathname: '/forgot-password'}}
										className={
											'btn-link text-danger ml-1'
										}>
											en cliquant ici
                                        </Link>
								</span>
							);
							break;
                        case 409:
                            setFlashbagMessage('La vérification du mot de passe ne correspond pas');
                            break;
                        case 400:
                        default:
                            setFlashbagMessage('Une erreur est survenue, veuillez essayer ultérieurement');
                            break;
                    }
                });
        }
    });

    function handleConfirmAdminPassword(event: ChangeEvent<HTMLInputElement>): void
    {
        const {value} = event.target;
        formik.setFieldValue('confirmAdminPassword', value);

        if (value.length >= 12) {
            setConfirmAdminPasswordValid(value === formik.values.newAdminPassword);
        }
    }

    function handleConfirmUserPassword(event: ChangeEvent<HTMLInputElement>): void
    {
        const {value} = event.target;
        formik.setFieldValue('confirmUserPassword', value);

        if (value.length > 3) {
            setConfirmUserPasswordValid(value === formik.values.newUserPassword);
        }
    }

    function handleChangeAddAnotherPasswordForUser(event: ChangeEvent<HTMLInputElement>): void
    {
        const {checked} = event.target;
        formik.setFieldValue('addAnotherPasswordForUser', checked);
        formik.setFieldValue('newUserPassword', checked ? '' : formik.values.newAdminPassword);
        formik.setFieldValue('confirmUserPassword', checked ? '' : formik.values.confirmAdminPassword);

    }

    useEffect(() => {
        const {addAnotherPasswordForUser, newAdminPassword, confirmAdminPassword} = formik.values;

        if (!addAnotherPasswordForUser) {
            formik.setFieldValue('newUserPassword', newAdminPassword);
            formik.setFieldValue('confirmUserPassword', confirmAdminPassword);
        }
    }, [formik.values]);

    return (
        <Container fluid>
            <Row style={{height: '100vh'}} className={'align-items-center'}>
                <Col xs={12} md={updatePasswordSuccess ? 4 : 6} className={'mx-auto'}>
                    <Row className={'mb-2'}>
                        <Col className={'text-center'}>
                            <Logo widthClass={'w-50'} />
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <Form onSubmit={(event: FormEvent<HTMLFormElement>): void => {
                                event.preventDefault();
                                formik.handleSubmit();
                            }}>
                                <Card>
                                    <Card.Header className={'bg-primary border-0 text-white font-weight-bold'}>
                                        Mise à jour de votre mot de passe
                                    </Card.Header>
                                    <Card.Body>
                                        {flashbag && (
                                            <Row>
                                                <Col>
                                                    <AlertCustom
                                                        classname={'text-left'}
                                                        variant={flashbagVariant}
                                                        heading={false}
														bodyContent={
															flashbagMessage
														}
                                                        transition={true}
                                                    />
                                                </Col>
                                            </Row>
                                        )}
                                        {!updatePasswordSuccess && (
                                            <>
                                                <Row>
                                                    <Col xs={12} md={6}>
                                                        <PasswordField
                                                            label={'Nouveau mot de passe administrateur'}
                                                            placeholder={'Nouveau mot de passe administrateur'}
                                                            isRequired={true}
                                                            name={'newAdminPassword'}
                                                            classNames={'mb-3'}
                                                            value={formik.values.newAdminPassword}
                                                            onChange={formik.handleChange}
                                                            error={getIn(formik.touched, 'newAdminPassword') && getIn(formik.errors, 'newAdminPassword')}
                                                        />
                                                        Pour créer un nouveau mot de passe,
                                                        saisissez un mot de passe avec :
                                                        <CheckList
                                                            items={passwordConstraints}
                                                            value={formik.values.newAdminPassword}
                                                        />
                                                    </Col>
                                                    <Col xs={12} md={6}>
                                                        <PasswordField
                                                            label={'Confirmation du mot de passe administrateur'}
                                                            placeholder={'Confirmer le nouveau mot de passe'}
                                                            isRequired={true}
                                                            name={'confirmAdminPassword'}
                                                            classNames={'mb-3'}
                                                            value={formik.values.confirmAdminPassword}
                                                            onChange={(event: ChangeEvent<HTMLInputElement>) => handleConfirmAdminPassword(event)}
                                                            error={
                                                                (!confirmAdminPasswordValid && formik.values.confirmAdminPassword.length >= 12 && 'Les mots de passe ne correspondent pas')
                                                                || (getIn(formik.touched, 'confirmAdminPassword') && getIn(formik.errors, 'confirmAdminPassword'))
                                                            }
                                                            success={confirmAdminPasswordValid}
                                                        />
                                                    </Col>
                                                </Row>
                                                <Row>
                                                    <Col>
                                                        <Checkbox
                                                            id={'addAnotherPasswordForUser'}
                                                            name={'addAnotherPasswordForUser'}
                                                            type={'checkbox'}
                                                            label={'Mot de passe différent pour les utilisateurs'}
                                                            checked={formik.values.addAnotherPasswordForUser}
                                                            onChange={(event: ChangeEvent<HTMLInputElement>) => handleChangeAddAnotherPasswordForUser(event)}
                                                            className={'text-md-right'}
                                                        />
                                                    </Col>
                                                </Row>
                                                <Row className={formik.values.addAnotherPasswordForUser ? '' : 'd-none'}>
                                                    <Col xs={10} className={'border-top my-3 mx-auto'}></Col>
                                                </Row>
                                                <Row className={formik.values.addAnotherPasswordForUser ? '' : 'd-none'}>
                                                    <Col xs={12} md={6}>
                                                        <PasswordField
                                                            label={'Nouveau mot de passe utilisateur'}
                                                            placeholder={'Nouveau mot de passe utilisateur'}
                                                            isRequired={true}
                                                            name={'newUserPassword'}
                                                            classNames={'mb-3'}
                                                            value={formik.values.newUserPassword}
                                                            onChange={formik.handleChange}
                                                            error={getIn(formik.touched, 'newUserPassword') && getIn(formik.errors, 'newUserPassword')}
                                                        />
                                                        Pour créer un nouveau mot de
                                                        passe, saisissez un mot de
                                                        passe avec :
                                                        <CheckList
                                                            items={passwordConstraints}
                                                            value={formik.values.newUserPassword}
                                                        />
                                                    </Col>
                                                    <Col xs={12} md={6}>
                                                        <PasswordField
                                                            label={'Confirmation du mot de passe utilisateur'}
                                                            placeholder={'Confirmer le nouveau mot de passe'}
                                                            isRequired={true}
                                                            name={'confirmUserPassword'}
                                                            classNames={'mb-3'}
                                                            value={formik.values.confirmUserPassword}
                                                            onChange={(event: ChangeEvent<HTMLInputElement>) => handleConfirmUserPassword(event)}
                                                            error={
                                                                (!confirmUserPasswordValid && formik.values.confirmUserPassword.length > 3 && 'Les mots de passe ne correspondent pas')
                                                                || (getIn(formik.touched, 'confirmUserPassword') && getIn(formik.errors, 'confirmUserPassword'))
                                                            }
                                                            success={confirmUserPasswordValid}
                                                        />
                                                    </Col>
                                                </Row>
                                                <Row className={'mt-3'}>
                                                    <Col className={'text-right'}>
                                                        <Button type={'submit'}>Mettre à jour</Button>
                                                    </Col>
                                                </Row>
                                            </>
                                        )}
                                        {updatePasswordSuccess && (
                                            <div className={'text-center'}>
                                                <span>Vous allez être redirigés automatiquement vers la page de connexion.</span>
                                                <br/>
                                                <span>Si ce n'est pas le cas veuillez cliquer <a href={'/'}>ici</a></span>
                                            </div>
                                        )}
                                    </Card.Body>
                                </Card>
                            </Form>
                        </Col>
                    </Row>
                </Col>
            </Row>
        </Container>
    )
}
